/**
 * Browser Fullscreen API helpers (web clients only — Electron uses native IPC).
 */

export function getFullscreenElement() {
  if (typeof document === 'undefined') {
    return null;
  }
  return (
    document.fullscreenElement
    || document.webkitFullscreenElement
    || document.msFullscreenElement
    || null
  );
}

export function isBrowserFullscreenActive() {
  return getFullscreenElement() != null;
}

export function isBrowserFullscreenSupported() {
  if (typeof document === 'undefined') {
    return false;
  }
  const root = document.documentElement;
  if (!root) {
    return false;
  }
  return typeof root.requestFullscreen === 'function'
    || typeof root.webkitRequestFullscreen === 'function';
}

export async function requestBrowserFullscreen() {
  if (typeof document === 'undefined') {
    throw new Error('requestBrowserFullscreen: document is not available');
  }
  if (!isBrowserFullscreenSupported()) {
    throw new Error('requestBrowserFullscreen: Fullscreen API is not supported');
  }

  const root = document.documentElement;
  if (typeof root.requestFullscreen === 'function') {
    await root.requestFullscreen();
    return;
  }
  if (typeof root.webkitRequestFullscreen === 'function') {
    root.webkitRequestFullscreen();
    return;
  }

  throw new Error('requestBrowserFullscreen: no fullscreen request method found');
}

export async function exitBrowserFullscreen() {
  if (typeof document === 'undefined') {
    throw new Error('exitBrowserFullscreen: document is not available');
  }

  if (typeof document.exitFullscreen === 'function') {
    await document.exitFullscreen();
    return;
  }
  if (typeof document.webkitExitFullscreen === 'function') {
    document.webkitExitFullscreen();
    return;
  }

  throw new Error('exitBrowserFullscreen: no fullscreen exit method found');
}

export async function toggleBrowserFullscreen() {
  if (isBrowserFullscreenActive()) {
    await exitBrowserFullscreen();
    return false;
  }
  await requestBrowserFullscreen();
  return true;
}

export const WEB_FULLSCREEN_BANNER_DISMISSED_KEY = 'coda_web_fullscreen_banner_dismissed';

export function markWebFullscreenBannerDismissed() {
  if (typeof localStorage === 'undefined') {
    throw new Error('markWebFullscreenBannerDismissed: localStorage is not available');
  }
  localStorage.setItem(WEB_FULLSCREEN_BANNER_DISMISSED_KEY, '1');
}

export function isWebFullscreenBannerDismissed() {
  if (typeof localStorage === 'undefined') {
    return false;
  }
  return localStorage.getItem(WEB_FULLSCREEN_BANNER_DISMISSED_KEY) === '1';
}
